import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { buildRejectedPreparationNote, toDiscardReason } from './qa-reject-discard.js';
import { uuidv7 } from 'uuidv7';
import { createActor } from 'xstate';
import * as z from 'zod';
import { CatalogType, AdministrationMode, PatientTherapySequenceStatus, PreparationType, ProductionDetailStep, ProductionWeighingStep, RestVialStatus, ReturnStatus, StockMovementSource, StockMovementType, } from '../../database/schema.js';
import { getSettings, queries } from '../../queries.js';
import { getProductionSource } from '../../util/production-source.js';
import { zql, } from '../../schema.js';
import { buildProductName, canEditValidatedPrescription, canRemovePatientTherapyValidation, canStartPatientTherapyRemake, getTherapySequenceInitialStatus, getPrescriptionRoles, } from '../../util/patients-therapies.js';
import { assertCan, assertIsAuthenticated, EventLogs, ForbiddenError } from '../../utils.js';
import { assertPreparationNotExpired, getSequenceUseBy, isUseByGuardedEvent } from '../../util/preparation-use-by.js';
import { nextPrescriptionInternalId } from '../patient/add-therapy-parameter.js';
import { copyDoseBasis } from '../patient/dose-basis.js';
import { createDiscard, createRestVialFromBatch, createReturnFromSequence, logAmmsRestVialDiscardMessage, } from '../stocks.mutators.js';
import { addEventLog, EventLogResources } from '../util.js';
import { itemsDetail, valueItem } from '../../util/event-log-items.js';
import { statusChangeItems } from '../patient/medicine-log-items.js';
import { getPatientCheck } from './production-log-items.js';
import { logAutomaticRelease } from '../utils/auto-release.js';
import { recordStockMovement } from '../utils/stock-movements.js';
import { createTherapySequence } from '../utils/therapy-sequence.js';
import { pickRestVialsToSpoil } from './spoilage-rest-vials.js';
import { AbortReason, assertValidationCarrier, logAmmsProductionCompleteMessage, PatientTherapySequenceEvent, PatientTherapySequenceEventType, PatientTherapySequenceMachine, pharmacistValidatePatientTherapySequence, QaRejectReason, SpoilageEventBody, startOrderProduction, StartProductionEventBody, SequenceEventPermissions, } from './state-machine.js';
// re-exported for '@cypro/engine/mutators' consumers
export { AbortReason, QaRejectReason };
async function findById(tx, ctx, id) {
    return await tx.run(queries.patientsTherapies.getSequenceById.fn({ args: id, ctx }));
}
export const SendEventSchema = z.object({
    id: z.uuidv7(),
    event: PatientTherapySequenceEvent,
});
// which validation to revoke; omitted keeps the legacy "remove the latest one" behaviour used by the prescription editor, while the drawer passes an explicit target per line
export const RemovablePatientTherapyValidation = z.enum(['doctor', 'pharmacist']);
export const RemovePatientTherapyValidationInput = z.object({
    id: z.uuidv7(),
    validation: RemovablePatientTherapyValidation.optional(),
});
async function fetchUserWithGroups(tx, ctx) {
    return await tx.run(queries.users.getUserById.fn({ ctx, args: ctx.user?.id || '' }));
}
export const AddPatientTherapySequenceObservation = z.object({
    sequenceId: z.uuidv7(),
    note: z.string().trim().nonempty(),
});
export const UpdateChairSchema = z.object({
    id: z.uuidv7(),
    chairId: z.string().nullable(),
});
// async function fetchUser(tx: Transaction, id: string | undefined) {
//   const user = await tx.run(zql.user.where('id', id).one())
//   if (!user) return null
//   const userGroups = await tx.run(zql.userGroups.where('userId', id).where('deletedAt', 'IS', null))
//   // we fetch the groups explicitly because sometimes zero reads the user from the cache, and the cached user might not have the groups populated, leading to not allowing the user to perform an action they should be able to perform
//   const groupIds = userGroups.map(ug => ug.groupId)
//   const groups = groupIds.length
//     ? await tx.run(zql.groups.where('id', 'IN', groupIds).where('deletedAt', 'IS', null))
//     : []
//   return {...user, groups}
// }
export const SwitchToVolumetricSchema = z.object({
    id: z.uuidv7(),
});
export const LogProductionStepSchema = z.object({
    sequenceId: z.uuidv7(),
    stepName: z.string().nonempty(),
    /** material check (SCAN) only: items confirmed with a click instead of a scan */
    confirmedWithoutScan: z.array(z.object({ name: z.string(), lot: z.string() })).optional(),
    /** material check (SCAN) only: barcodes that matched nothing */
    wrongScans: z.int().min(0).optional(),
});
export const LabelPrintPlace = z.enum(['cleanroom', 'prescription', 'qa']);
export const LogLabelPrintSchema = z.object({
    sequenceId: z.uuidv7(),
    place: LabelPrintPlace,
});
export const LogProductionStepReweighSchema = z.object({
    sequenceId: z.uuidv7(),
    step: ProductionWeighingStep,
    oldWeightGrams: z.number(),
    newWeightGrams: z.number(),
    reason: z.string().trim().max(500).optional(),
});
export const LogProductionStepRedoneSchema = z.object({
    sequenceId: z.uuidv7(),
    step: ProductionWeighingStep,
});
export const RecordPreparationStartSchema = z.object({
    sequenceId: z.uuidv7(),
});
export const ABORT_DRUG_STATUSES = ['UNUSED', 'PARTIALLY_CONSUMED', 'SCRAPPED'];
export const ABORT_CONSUMABLE_STATUSES = ['INTACT', 'UNSEALED'];
export const AbortMaterialStatus = z.enum([...ABORT_DRUG_STATUSES, ...ABORT_CONSUMABLE_STATUSES]);
export const ABORT_MATERIAL_KIND = ['batch', 'restVial', 'return'];
export const AbortMaterialKind = z.enum(ABORT_MATERIAL_KIND);
export const AbortProductionSchema = z.object({
    sequenceId: z.uuidv7(),
    reason: AbortReason.nonoptional(),
    remake: z.boolean(),
    // the drawer's cancel-production aborts to `cancelled`; the cleanroom aborts to `discarded`
    cancel: z.boolean(),
    materials: z
        .array(z.object({
        itemId: z.uuidv7(),
        kind: AbortMaterialKind,
        status: AbortMaterialStatus,
        remainingWeightVolume: z.number().optional(),
    }))
        .nonempty()
        .superRefine((materials, ctx) => {
        materials.forEach((material, index) => {
            if (material.status === AbortMaterialStatus.enum.PARTIALLY_CONSUMED &&
                (material.remainingWeightVolume == null || material.remainingWeightVolume <= 0)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['materials', index, 'remainingWeightVolume'],
                    message: 'remainingWeightVolume is required when partially consumed',
                });
            }
        });
    }),
});
export const RemakePatientTherapySequenceSchema = z.object({ id: z.uuidv7() });
export const SaveCleanroomStateSchema = z.object({
    sequenceId: z.uuidv7(),
    // the persisted XState snapshot, boxed with a version by the cleanroom app
    state: z.record(z.string(), z.json()),
});
// A bag past its use-by must not be released or administered (CY-327); checked with server time before any side effect.
async function assertSequenceNotExpired(tx, ctx, sequence, eventType) {
    if (!isUseByGuardedEvent(eventType))
        return;
    const [source, statusLogs] = await Promise.all([
        tx.run(queries.patientsTherapies.getPreparationLabelSource.fn({ args: sequence.id, ctx })),
        tx.run(queries.patientsTherapies.getSequenceStatusLogs.fn({ args: sequence.id, ctx })),
    ]);
    const useBy = source ? getSequenceUseBy(source, statusLogs) : null;
    assertPreparationNotExpired({ eventType, internalId: sequence.internalId, useBy, now: Date.now() });
}
/** The state only exists while the preparation is resumable; completing or aborting drops it. */
async function deleteCleanroomState(tx, sequenceId) {
    const existing = await tx.run(zql.cleanroomStates.where('therapySequenceId', '=', sequenceId).one());
    if (existing)
        await tx.mutate.cleanroomStates.delete({ id: existing.id });
}
export const patientsTherapies = {
    sendEvent: defineMutator(SendEventSchema, async ({ args, tx, ctx }) => {
        if (tx.location !== 'server')
            return;
        assertIsAuthenticated(ctx);
        const sequence = await findById(tx, ctx, args.id);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        // const user = await fetchUser(tx, ctx.user?.id)
        const user = await tx.run(queries.users.getUserById.fn({ ctx, args: ctx.user?.id || '' }));
        if (!user)
            throw new Error('User not found.');
        const settings = await getSettings(tx);
        const snapshot = PatientTherapySequenceMachine.resolveState({ user, sequence, settings });
        if (!snapshot)
            throw new Error('Snapshot could not be resolved.');
        // Every event is checked against its permission before any side effect runs.
        if (!snapshot.can(args.event)) {
            throw new ForbiddenError(SequenceEventPermissions[args.event.type]);
        }
        await assertSequenceNotExpired(tx, ctx, sequence, args.event.type);
        const actor = createActor(PatientTherapySequenceMachine.instance, {
            snapshot,
            input: snapshot.context,
        });
        actor.start();
        actor.send(args.event);
        const actorStatus = actor.getSnapshot().value;
        const updates = {
            pharmacistValidatedBy: sequence.pharmacistValidatedBy,
            pharmacistValidatedAt: sequence.pharmacistValidatedAt,
            patientArrivedAt: sequence.patientArrivedAt,
            doctorValidatedBy: sequence.doctorValidatedBy,
            doctorValidatedAt: sequence.doctorValidatedAt,
            administeredBy: sequence.administeredBy,
            administeredAt: sequence.administeredAt,
            administrationMode: sequence.administrationMode,
            patientScanSkipped: sequence.patientScanSkipped,
            administeredOnDispatch: sequence.administeredOnDispatch,
            producedAt: sequence.producedAt,
            releasedAt: sequence.releasedAt,
            producedBy: sequence.producedBy,
            productionSource: sequence.productionSource,
            restVialCreated: sequence.restVialCreated,
            qaResult: sequence.qaResult,
            prescriptionSource: sequence.prescriptionSource,
            returnedBy: sequence.returnedBy,
            returnedAt: sequence.returnedAt,
            cancelledBy: sequence.cancelledBy,
            cancelledAt: sequence.cancelledAt,
            cancellationReason: sequence.cancellationReason,
            discardedBy: sequence.discardedBy,
            discardedAt: sequence.discardedAt,
            discardReason: sequence.discardReason,
            discardNote: sequence.discardNote,
            // remadeBy/remadeAt are written by remakeTherapySequence; copying the old values here would overwrite them
        };
        const logItems = [];
        let logStatus;
        if (args.event.type === PatientTherapySequenceEventType.enum.PHARMACIST_VALIDATE) {
            if (args.event.body) {
                const roles = getPrescriptionRoles(user.groups);
                const validationLogItems = await pharmacistValidatePatientTherapySequence(tx, sequence, args.event.body, ctx.user.id, canEditValidatedPrescription(roles));
                logItems.push(...validationLogItems);
            }
            updates.pharmacistValidatedBy = ctx.user.id;
            updates.pharmacistValidatedAt = Date.now();
        }
        if (args.event.type === PatientTherapySequenceEventType.enum.DOCTOR_VALIDATE) {
            // the doctor event has no body: check the stored carrier, volume and method
            assertValidationCarrier(sequence, {});
            updates.doctorValidatedBy = ctx.user.id;
            updates.doctorValidatedAt = Date.now();
            // snapshot what the doctor released per medicine so a later pharmacist change cannot rewrite it
            const prescribedAt = Date.now();
            await Promise.all((sequence.medicine ?? []).map(medicine => tx.mutate.patientTherapySequenceMedicine.update({
                id: medicine.id,
                doctorPrescribedNameId: medicine.prescribedNameId ?? null,
                doctorPrescribedActiveSubstanceId: medicine.activeSubstanceId ?? null,
                doctorPrescribedTotalAmount: medicine.totalAmount ?? null,
                doctorPrescribedAt: prescribedAt,
                updatedAt: prescribedAt,
                updatedBy: ctx.user.id,
            })));
        }
        if (args.event.type === PatientTherapySequenceEventType.enum.START_PRODUCTION) {
            // await startOrderProduction(tx, args.id, args.event.body)
            throw new Error('Not implemented: Starting production is currently handled by a separate endpoint.');
        }
        // Patient arrival only applies while the `doctorRelease` setting keeps the step enabled.
        if (args.event.type === PatientTherapySequenceEventType.enum.PATIENT_ARRIVED && settings?.doctorRelease !== false) {
            updates.patientArrivedAt = Date.now();
        }
        if (args.event.type === PatientTherapySequenceEventType.enum.REVERSE_PATIENT_ARRIVED) {
            updates.patientArrivedAt = null;
        }
        if (args.event.type === PatientTherapySequenceEventType.enum.RETURN_TO_PHARMACY) {
            await createReturnFromSequence(tx, {
                sequence,
                stockLocationId: args.event.body.stockLocationId,
                reason: args.event.body.reason,
                note: args.event.body.note,
            }, ctx);
            updates.returnedBy = ctx.user.id;
            updates.returnedAt = Date.now();
            // a preparation marked Administered on dispatch was never given, so it no longer counts as administered
            if (sequence.status === PatientTherapySequenceStatus.enum.administered) {
                updates.administeredBy = null;
                updates.administeredAt = null;
                updates.administeredOnDispatch = null;
            }
        }
        if (args.event.type === PatientTherapySequenceEventType.enum.ADMINISTER) {
            updates.administeredBy = ctx.user.id;
            updates.administeredAt = Date.now();
            updates.administeredOnDispatch = false;
            const mode = args.event.body?.mode ?? null;
            updates.administrationMode = mode;
            // The patient scan only exists in Scan mode; Manual has nothing to skip.
            updates.patientScanSkipped =
                mode === AdministrationMode.enum.SCAN ? Boolean(args.event.body?.patientScanSkipped) : null;
            // how the patient was identified; anything but a scan is a warning
            const patientCheck = getPatientCheck(mode, updates.patientScanSkipped);
            if (patientCheck) {
                logItems.push(...valueItem('patient_check', patientCheck, { valueKey: 'event.value.patient_check.{value}' }));
                logStatus = patientCheck === 'scanned' ? 'OK' : 'WARNING';
            }
        }
        if (args.event.type === PatientTherapySequenceEventType.enum.COMPLETE_PRODUCTION) {
            // the Reports date bases Produced on / Released on read these stamps (CY-356)
            updates.producedAt = Date.now();
            // Reports filters: who produced it and whether it drew from a new pack, a rest vial or both (CY-353)
            updates.producedBy = ctx.user.id;
            updates.productionSource = getProductionSource(sequence) ?? null;
            if (!sequence.order) {
                throw new Error(`Cannot complete production: therapy sequence ${sequence.id} has no order.`);
            }
            // before the AMMS report, which reads the DRUG weighings for the amount drawn per batch
            await recordProductionWeighings(tx, sequence.id, args.event.body?.weighings ?? [], ctx.user.id);
            await logAmmsProductionCompleteMessage(tx, {
                sequence,
                medicine: sequence.medicine.map(medicine => ({
                    id: medicine.id,
                    activeSubstanceId: medicine.activeSubstanceId,
                    prescribedNameId: medicine.prescribedNameId ?? '',
                    totalAmount: medicine.totalAmount,
                    calculationMethod: medicine.calculationMethod,
                })),
                order: sequence.order,
                pharmacistUserId: ctx.user.id,
                now: Date.now(),
            });
            await logProductionEvent(tx, {
                sequence,
                event: EventLogs.PRODUCTION_COMPLETED,
                userId: ctx.user.id,
            });
            await deleteCleanroomState(tx, sequence.id);
        }
        if (args.event.type === PatientTherapySequenceEventType.enum.RELEASE_FOR_DELIVERY) {
            updates.releasedAt = Date.now();
            // reaching QA & dispatch and releasing is the passed outcome (CY-353)
            updates.qaResult = 'passed';
            await logProductionEvent(tx, {
                sequence,
                event: EventLogs.PRODUCTION_RELEASED,
                userId: ctx.user.id,
            });
        }
        if (args.event.type === PatientTherapySequenceEventType.enum.SPOILAGE) {
            // a rejection at QA & dispatch is the rejected outcome; aborting in production is not a QA result
            if (sequence.status === PatientTherapySequenceStatus.enum.qa_dispatch)
                updates.qaResult = 'rejected';
            await spoilProduction(tx, sequence, args.event.body, ctx);
            updates.discardedBy = ctx.user.id;
            updates.discardedAt = Date.now();
            updates.discardReason = args.event.body.reason;
            updates.discardNote = args.event.body.note || null;
        }
        let finalStatus = sequence.status;
        if (typeof actorStatus !== 'string')
            finalStatus = PatientTherapySequenceStatus.enum.validating;
        else
            finalStatus = actorStatus;
        // Auto-release for delivery: completing production skipped QA & dispatch (CY-263)
        const isAutoRelease = args.event.type === PatientTherapySequenceEventType.enum.COMPLETE_PRODUCTION &&
            finalStatus !== PatientTherapySequenceStatus.enum.qa_dispatch;
        // Mark preparations as administered on dispatch: the release time is the administration time
        if (finalStatus === PatientTherapySequenceStatus.enum.administered &&
            args.event.type !== PatientTherapySequenceEventType.enum.ADMINISTER) {
            // the releasing user is recorded as Administered by, marked on dispatch (CY-332)
            updates.administeredBy = ctx.user.id;
            updates.administeredAt = Date.now();
            updates.administeredOnDispatch = true;
        }
        // an automatic release (Settings > Workflow) releases in the same step as the completion
        if (isAutoRelease)
            updates.releasedAt = Date.now();
        const updatedPatientTherapySequence = { id: args.id, status: finalStatus, ...updates };
        await tx.mutate.patientTherapySequence.update(updatedPatientTherapySequence);
        // if (tx.location === 'server') {
        const patches = createPatch(sequence, updatedPatientTherapySequence);
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: updatedPatientTherapySequence.id,
            action: 'update',
            domain: 'prescription',
            event: EventLogs.PRESCRIPTION_STATUS_CHANGE,
            patches,
            performedBy: ctx.user.id,
            performedAt: Date.now(),
            ...(logStatus && { status: logStatus }),
            details: {
                therapyInternalId: sequence.internalId ?? '',
                action: args.event.type,
                // Shown in the prescription's activity log; strings, as event log details are string-valued.
                ...(args.event.type === PatientTherapySequenceEventType.enum.ADMINISTER && updates.administrationMode
                    ? {
                        administrationMode: updates.administrationMode,
                        patientScanSkipped: updates.patientScanSkipped == null ? null : String(updates.patientScanSkipped),
                    }
                    : {}),
                ...itemsDetail([...statusChangeItems(sequence.status, finalStatus), ...logItems]),
            },
        }, ctx);
        if (isAutoRelease) {
            await logProductionEvent(tx, { sequence, event: EventLogs.PRODUCTION_RELEASED, userId: ctx.user.id });
            await logAutomaticRelease(tx, {
                sequenceId: sequence.id,
                therapyInternalId: sequence.internalId,
                status: finalStatus,
                userId: ctx.user.id,
                now: Date.now(),
            });
        }
        // }
        // }
    }),
    // Removes a validation line; without a `validation` target it removes the latest. Blocked after production.
    removePatientTherapyValidation: defineMutator(RemovePatientTherapyValidationInput, async ({ args, tx, ctx }) => {
        assertIsAuthenticated(ctx);
        const sequence = await findById(tx, ctx, args.id);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        const user = await fetchUserWithGroups(tx, ctx);
        const roles = getPrescriptionRoles(user?.groups);
        if (!canRemovePatientTherapyValidation(sequence, args.validation, roles)) {
            throw new Error('You cannot remove this validation.');
        }
        const updated = { id: args.id, updatedAt: Date.now(), updatedBy: ctx.user.id };
        const removesPharmacist = args.validation === RemovablePatientTherapyValidation.enum.pharmacist ||
            (!args.validation && Boolean(sequence.pharmacistValidatedAt));
        assertCan(ctx, removesPharmacist ? 'prescriptions.validate_pharmacist' : 'prescriptions.validate_doctor');
        if (removesPharmacist) {
            Object.assign(updated, {
                pharmacistValidatedBy: null,
                pharmacistValidatedAt: null,
                status: PatientTherapySequenceStatus.enum.validating,
            });
        }
        else {
            Object.assign(updated, {
                doctorValidatedBy: null,
                doctorValidatedAt: null,
                ...(sequence.pharmacistValidatedAt ? { pharmacistValidatedBy: null, pharmacistValidatedAt: null } : {}),
                status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
            });
        }
        const removed = removesPharmacist
            ? RemovablePatientTherapyValidation.enum.pharmacist
            : RemovablePatientTherapyValidation.enum.doctor;
        await tx.mutate.patientTherapySequence.update(updated);
        if (tx.location === 'server') {
            const patches = createPatch(sequence, updated);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: args.id,
                action: 'update',
                domain: 'prescription',
                event: EventLogs.PRESCRIPTION_VALIDATION_REMOVED,
                patches,
                performedBy: ctx.user.id,
                performedAt: Date.now(),
                details: {
                    therapyInternalId: sequence.internalId ?? '',
                    validationRemoved: removed,
                },
            }, ctx);
        }
    }),
    addObservation: defineMutator(AddPatientTherapySequenceObservation, async ({ args, tx, ctx }) => {
        assertIsAuthenticated(ctx);
        const sequence = await findById(tx, ctx, args.sequenceId);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        const userActions = {
            createdAt: Date.now(),
            createdBy: ctx.user.id,
        };
        const newObservation = {
            id: uuidv7(),
            therapySequenceId: args.sequenceId,
            note: args.note,
            ...userActions,
        };
        const promises = [tx.mutate.patientTherapySequenceObservations.insert(newObservation)];
        if (tx.location === 'server') {
            const patches = createPatch({}, newObservation);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequenceObservation,
                resourceId: newObservation.id,
                action: 'create',
                domain: 'prescription',
                status: 'OK',
                patches: patches,
                performedBy: userActions.createdBy,
                performedAt: userActions.createdAt,
                details: { therapyInternalId: sequence.internalId ?? '', note: args.note.slice(0, 200) },
            }, ctx));
        }
        await Promise.all(promises);
    }),
    updateChair: defineMutator(UpdateChairSchema, async ({ args, tx, ctx }) => {
        assertIsAuthenticated(ctx);
        const sequence = await findById(tx, ctx, args.id);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        const updated = {
            id: args.id,
            chairId: args.chairId,
            updatedAt: Date.now(),
            updatedBy: ctx.user.id,
        };
        await tx.mutate.patientTherapySequence.update(updated);
        if (tx.location === 'server') {
            const patches = createPatch({ chairId: sequence.chairId }, { chairId: args.chairId });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: updated.id,
                action: 'update',
                domain: 'prescription',
                event: 'chair_updated',
                patches,
                performedBy: ctx.user.id,
                performedAt: updated.updatedAt,
            }, ctx);
        }
    }),
    createOrder: defineMutator(StartProductionEventBody, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.produce');
        await startOrderProduction(tx, args, ctx.user.id);
    }),
    switchToVolumetric: defineMutator(SwitchToVolumetricSchema, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.produce');
        const sequence = await findById(tx, ctx, args.id);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        if (sequence.preparationType === PreparationType.enum.VOLUMETRIC)
            return;
        const now = Date.now();
        const updated = {
            id: args.id,
            preparationType: PreparationType.enum.VOLUMETRIC,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.patientTherapySequence.update(updated);
        // The saved cleanroom progress belongs to the gravimetric graph; drop it so no client restores
        // the scale steps after the switch.
        await deleteCleanroomState(tx, args.id);
        if (tx.location === 'server') {
            const patches = createPatch({ preparationType: sequence.preparationType || '-' }, { preparationType: updated.preparationType });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: updated.id,
                action: 'update',
                domain: 'prescription',
                event: 'preparation_type_changed',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                details: {
                    reason: 'scale_unavailable',
                    therapyInternalId: sequence.internalId ?? '',
                },
            });
        }
    }),
    logProductionStep: defineMutator(LogProductionStepSchema, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.produce');
        if (tx.location !== 'server')
            return;
        const sequence = await findById(tx, ctx, args.sequenceId);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        if (!sequence.orderId)
            return;
        const unscanned = args.confirmedWithoutScan ?? [];
        const items = [
            ...unscanned.map(({ name, lot }) => ({ field: 'confirmed_without_scan', value: `${name}, lot ${lot}` })),
            ...(args.wrongScans ? valueItem('wrong_scans', args.wrongScans) : []),
        ];
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.order,
            resourceId: sequence.orderId,
            action: 'update',
            domain: 'production',
            event: EventLogs.PRODUCTION_STEP_COMPLETED,
            patches: createPatch({}, { stepName: args.stepName, orderInternalId: sequence.order?.internalId }),
            performedBy: ctx.user.id,
            performedAt: Date.now(),
            status: unscanned.length > 0 ? 'WARNING' : 'OK',
            details: {
                orderInternalId: sequence.order?.internalId,
                stepName: args.stepName,
                ...itemsDetail(items),
            },
        }, ctx);
    }),
    // The browser print dialog reports no copies or printer: this records that printing was started.
    logLabelPrint: defineMutator(LogLabelPrintSchema, async ({ args, tx, ctx }) => {
        assertIsAuthenticated(ctx);
        if (tx.location !== 'server')
            return;
        const sequence = await findById(tx, ctx, args.sequenceId);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        const printedBefore = await tx.run(zql.eventLogs
            .where('resourceId', '=', sequence.id)
            .where('event', 'IN', [EventLogs.LABEL_PRINTED, EventLogs.LABEL_REPRINTED])
            .one());
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: sequence.id,
            action: 'access',
            domain: 'production',
            event: printedBefore ? EventLogs.LABEL_REPRINTED : EventLogs.LABEL_PRINTED,
            performedBy: ctx.user.id,
            performedAt: Date.now(),
            status: 'OK',
            details: {
                therapyInternalId: sequence.internalId ?? '',
                ...itemsDetail(valueItem('place', args.place, { valueKey: 'event.value.place.{value}' })),
            },
        }, ctx);
    }),
    logProductionStepReweigh: defineMutator(LogProductionStepReweighSchema, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.produce');
        if (tx.location !== 'server')
            return;
        const sequence = await findById(tx, ctx, args.sequenceId);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        const now = Date.now();
        // a test op carries the replaced reading so the history drawer can compare both weights
        const patches = [
            { op: 'test', path: '/weightGrams', value: args.oldWeightGrams },
            { op: 'replace', path: '/weightGrams', value: args.newWeightGrams },
            { op: 'add', path: '/step', value: args.step },
            ...(args.reason ? [{ op: 'add', path: '/reason', value: args.reason }] : []),
        ];
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: sequence.id,
            action: 'update',
            domain: 'production',
            event: EventLogs.PRODUCTION_STEP_REWEIGHED,
            patches,
            performedBy: ctx.user.id,
            performedAt: now,
            status: 'OK',
            details: {
                orderInternalId: sequence.order?.internalId,
                ...(args.reason ? { reason: args.reason } : {}),
                ...itemsDetail([
                    ...valueItem('stepName', args.step, { valueKey: 'event.value.step.{value}' }),
                    { field: 'weight', old: args.oldWeightGrams, new: args.newWeightGrams, unit: 'g' },
                ]),
            },
        }, ctx);
    }),
    logProductionStepRedone: defineMutator(LogProductionStepRedoneSchema, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.produce');
        if (tx.location !== 'server')
            return;
        const sequence = await findById(tx, ctx, args.sequenceId);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: sequence.id,
            action: 'update',
            domain: 'production',
            event: EventLogs.PRODUCTION_STEP_REDONE,
            patches: createPatch({}, { stepName: args.step }),
            performedBy: ctx.user.id,
            performedAt: Date.now(),
            status: 'OK',
            details: {
                orderInternalId: sequence.order?.internalId,
                ...itemsDetail(valueItem('stepName', args.step, { valueKey: 'event.value.step.{value}' })),
            },
        }, ctx);
    }),
    recordPreparationStart: defineMutator(RecordPreparationStartSchema, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.produce');
        if (tx.location !== 'server')
            return;
        const sequence = await findById(tx, ctx, args.sequenceId);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        const now = Date.now();
        // a production restarted after an abort redoes the material check, so the latest start wins
        const existing = await tx.run(zql.productionDetails
            .where('therapySequenceId', '=', sequence.id)
            .where('step', '=', ProductionDetailStep.enum.PREPARATION_START)
            .where('deletedAt', 'IS', null)
            .one());
        if (existing) {
            await tx.mutate.productionDetails.update({
                id: existing.id,
                recordedAt: now,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
            return;
        }
        await tx.mutate.productionDetails.insert({
            id: uuidv7(),
            therapySequenceId: sequence.id,
            medicineId: null,
            step: ProductionDetailStep.enum.PREPARATION_START,
            substanceIndex: 0,
            actualWeightGrams: null,
            recordedAt: now,
            createdAt: now,
            createdBy: ctx.user.id,
        });
    }),
    saveCleanroomState: defineMutator(SaveCleanroomStateSchema, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.produce');
        // upsert needs a read, so only the server writer does the work
        if (tx.location !== 'server')
            return;
        const sequence = await tx.run(zql.patientTherapySequence.where('id', '=', args.sequenceId).one());
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        // completed/aborted preparations no longer need a resumable state
        if (sequence.status !== PatientTherapySequenceStatus.enum.in_production)
            return;
        const now = Date.now();
        const existing = await tx.run(zql.cleanroomStates.where('therapySequenceId', '=', args.sequenceId).one());
        if (existing) {
            await tx.mutate.cleanroomStates.update({
                id: existing.id,
                state: args.state,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
            return;
        }
        await tx.mutate.cleanroomStates.insert({
            id: uuidv7(),
            therapySequenceId: args.sequenceId,
            state: args.state,
            createdAt: now,
            createdBy: ctx.user.id,
            updatedAt: now,
            updatedBy: ctx.user.id,
        });
    }),
    abortProduction: defineMutator(AbortProductionSchema, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.produce');
        if (tx.location !== 'server')
            return;
        const sequence = await findById(tx, ctx, args.sequenceId);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        if (sequence.status !== PatientTherapySequenceStatus.enum.in_production)
            throw new Error('Cannot abort a prescription that is not in production.');
        const now = Date.now();
        const batchMaterials = args.materials.filter(m => m.kind === 'batch');
        const restVialMaterials = args.materials.filter(m => m.kind === 'restVial');
        const returnMaterials = args.materials.filter(m => m.kind === 'return');
        const batchIds = [...new Set(batchMaterials.map(m => m.itemId))];
        const restVialIds = [...new Set(restVialMaterials.map(m => m.itemId))];
        const returnIds = [...new Set(returnMaterials.map(m => m.itemId))];
        const batches = batchIds.length
            ? await tx.run(zql.batches
                .where('id', 'IN', batchIds)
                .related('tradeName', t => t.related('medicationProfile', p => p.related('drugCatalogItem'))))
            : [];
        const restVials = restVialIds.length ? await tx.run(zql.restVials.where('id', 'IN', restVialIds)) : [];
        const returns = returnIds.length ? await tx.run(zql.returns.where('id', 'IN', returnIds)) : [];
        // Serialized: multiple PARTIALLY_CONSUMED units of the same or different batches
        // create rest vials, and getRestVialInternalId must not run concurrently.
        await batchIds.reduce(async (chain, batchId) => {
            await chain;
            const batch = batches.find(b => b.id === batchId);
            if (!batch)
                throw new Error(`Batch not found: ${batchId}`);
            await applyAbortBatchEffect(tx, { batch, materials: batchMaterials.filter(m => m.itemId === batchId), sequenceId: args.sequenceId }, ctx);
        }, Promise.resolve());
        await Promise.all(restVialMaterials.map(async (material) => {
            const restVial = restVials.find(r => r.id === material.itemId);
            if (!restVial)
                throw new Error(`Rest vial not found: ${material.itemId}`);
            await applyAbortRestVialEffect(tx, { restVial, material }, ctx);
        }));
        await Promise.all(returnMaterials.map(async (material) => {
            const returnItem = returns.find(r => r.id === material.itemId);
            if (!returnItem)
                throw new Error(`Return not found: ${material.itemId}`);
            await applyAbortReturnEffect(tx, { returnItem, material }, ctx);
        }));
        // cancel-production aborts to `cancelled`, the cleanroom abort to `discarded`
        const abortedFields = args.cancel
            ? {
                status: PatientTherapySequenceStatus.enum.cancelled,
                cancelledBy: ctx.user.id,
                cancelledAt: now,
                cancellationReason: args.reason,
            }
            : {
                status: PatientTherapySequenceStatus.enum.discarded,
                discardedBy: ctx.user.id,
                discardedAt: now,
                discardReason: args.reason,
            };
        await tx.mutate.patientTherapySequence.update({
            id: args.sequenceId,
            ...abortedFields,
            updatedAt: now,
            updatedBy: ctx.user.id,
        });
        await deleteCleanroomState(tx, args.sequenceId);
        // a remake spawns a fresh prescription; the aborted one still ends up cancelled/discarded
        if (args.remake) {
            await remakeTherapySequence(tx, sequence, ctx);
        }
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: args.sequenceId,
            action: 'update',
            domain: 'production',
            event: EventLogs.PRODUCTION_ABORTED,
            performedBy: ctx.user.id,
            performedAt: now,
            status: 'WARNING',
            details: {
                orderInternalId: sequence.order?.internalId,
                reason: args.reason,
                remake: String(args.remake),
            },
        }, ctx);
    }),
    // Start a remake of a discarded prescription, mirroring abort + remake; blocked once a remake exists.
    remakePatientTherapySequence: defineMutator(RemakePatientTherapySequenceSchema, async ({ args, tx, ctx }) => {
        assertCan(ctx, 'production.release');
        const sequence = await findById(tx, ctx, args.id);
        if (!sequence)
            throw new Error('Therapy sequence not found.');
        const user = await fetchUserWithGroups(tx, ctx);
        const roles = getPrescriptionRoles(user?.groups);
        if (!canStartPatientTherapyRemake(sequence, roles)) {
            throw new Error('Only a pharmacist can remake a discarded prescription that has not been remade yet.');
        }
        await remakeTherapySequence(tx, sequence, ctx);
    }),
};
async function spoilProduction(tx, sequence, body, ctx) {
    assertIsAuthenticated(ctx);
    const now = Date.now();
    // Discard products created from this prescription: the ticked Available rest vials and the returns.
    const restVialIds = [...new Set(body.restVialIds)];
    const [loadedRestVials, returns] = await Promise.all([
        restVialIds.length ? tx.run(zql.restVials.where('id', 'IN', restVialIds)) : Promise.resolve([]),
        tx.run(zql.returns.where('sequenceId', '=', sequence.id)),
    ]);
    // Checked before any write: a vial In use or Used by another preparation must never be discarded (CY-329).
    const restVials = pickRestVialsToSpoil(loadedRestVials, sequence.id, restVialIds);
    await Promise.all(restVials.map(async (restVial) => {
        const updatedRestVial = {
            id: restVial.id,
            status: RestVialStatus.enum.DISCARDED,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.restVials.update(updatedRestVial);
        await createDiscard(tx, { type: 'restVial', id: restVial.id, stockLocationId: restVial.stockLocationId }, 'SPOILED', ctx);
        await logAmmsRestVialDiscardMessage(tx, { restVialId: restVial.id, now });
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.restVial,
            resourceId: restVial.id,
            action: 'update',
            patches: createPatch(restVial, updatedRestVial),
            performedBy: ctx.user.id,
            performedAt: now,
            domain: 'production',
            status: 'OK',
            details: {
                sequenceId: sequence.id,
            },
        }, ctx);
    }));
    await Promise.all(returns.map(async (ret) => {
        const updatedReturn = {
            id: ret.id,
            status: ReturnStatus.enum.DISCARDED,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.returns.update(updatedReturn);
        await createDiscard(tx, { type: 'return', id: ret.id, stockLocationId: ret.stockLocationId }, 'SPOILED', ctx);
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.return,
            resourceId: ret.id,
            action: 'update',
            patches: createPatch(ret, updatedReturn),
            performedBy: ctx.user.id,
            performedAt: now,
            domain: 'production',
            status: 'OK',
            details: {
                sequenceId: sequence.id,
            },
        }, ctx);
    }));
    // The rejected preparation itself: recorded as a discarded return, so Discards, waste and cost reports
    // list the bag with its patient, substances, amounts, reason, user and time (CY-330).
    await createReturnFromSequence(tx, {
        sequence,
        stockLocationId: null,
        discardedAs: { reason: toDiscardReason(body.reason), note: buildRejectedPreparationNote(body.reason, body.note) },
    }, ctx);
    if (body.remake) {
        await remakeTherapySequence(tx, sequence, ctx);
    }
    await addEventLog(tx, {
        id: uuidv7(),
        resource: EventLogResources.patientTherapySequence,
        resourceId: sequence.id,
        action: 'update',
        domain: 'production',
        event: EventLogs.PRODUCTION_SPOILED,
        performedBy: ctx.user.id,
        performedAt: now,
        status: 'WARNING',
        details: {
            orderInternalId: sequence.order?.internalId,
            remake: String(body.remake),
            reason: body.reason ?? null,
        },
    }, ctx);
}
async function remakeTherapySequence(tx, sequence, ctx) {
    assertIsAuthenticated(ctx);
    const fullSequence = await tx.run(zql.patientTherapySequence
        .where('id', '=', sequence.id)
        .related('medicine', q => q.where('deletedAt', 'IS', null))
        .one());
    if (!fullSequence)
        throw new Error('Therapy sequence not found.');
    const medicines = fullSequence.medicine ?? [];
    const { internalId } = await nextPrescriptionInternalId(tx, new Date(sequence.therapyDate).getTime());
    const [activeSubstances, prescribedNames] = await Promise.all([
        tx.run(zql.drugCatalogItems
            .where('catalogType', CatalogType.enum.ACTIVE_SUBSTANCE)
            .where('id', 'IN', medicines.map(m => m.activeSubstanceId).filter((id) => Boolean(id)))
            .related('activeSubstanceDetails')),
        tx.run(zql.prescribedNames
            .where('id', 'IN', medicines.map(m => m.prescribedNameId).filter((id) => Boolean(id)))
            .related('drugCatalogItem', item => item.related('activeSubstanceDetails'))),
    ]);
    const requiresAdditionalApproval = Boolean(activeSubstances.some(s => s.activeSubstanceDetails?.additionalApprovalRequired) ||
        prescribedNames.some(pn => pn.drugCatalogItem?.activeSubstanceDetails?.additionalApprovalRequired));
    const { id: remadeSequenceId } = await createTherapySequence(tx, {
        data: {
            patientId: sequence.patientId,
            protocolId: sequence.protocolId,
            patientProtocolId: sequence.patientProtocolId,
            sequenceGroupId: sequence.sequenceGroupId,
            internalId,
            therapyDate: sequence.therapyDate,
            status: getTherapySequenceInitialStatus(requiresAdditionalApproval),
            isNote: sequence.isNote,
            note: sequence.note,
            therapyNote: sequence.therapyNote,
            pharmacyNote: sequence.pharmacyNote,
            cycle: sequence.cycle,
            day: sequence.day,
            time: sequence.time,
            carrierSolutionId: sequence.carrierSolutionId,
            applicationFormId: sequence.applicationFormId,
            orderId: null,
            duration: sequence.duration,
            volume: sequence.volume,
            volumeMode: sequence.volumeMode,
            chairId: sequence.chairId,
            preparationType: sequence.preparationType,
            responsibleDoctorId: sequence.responsibleDoctorId,
            // the amounts are copied, so the values they were calculated with are too; both stay until the doctor
            // changes the dose (CY-303)
            ...copyDoseBasis(sequence),
            doseCopiedFromSequenceId: sequence.id,
        },
        medicines: medicines.map(m => ({
            patientId: m.patientId,
            therapyDate: m.therapyDate,
            activeSubstanceId: m.activeSubstanceId,
            prescribedNameId: m.prescribedNameId,
            autIdem: m.autIdem,
            formType: m.formType,
            calculationMethod: m.calculationMethod,
            regularDose: m.regularDose,
            recommendedDose: m.recommendedDose,
            precisionDecimals: m.precisionDecimals,
            doseProportion: m.doseProportion,
            totalAmount: m.totalAmount,
            preparationType: m.preparationType,
            notes: m.notes,
            ageRanges: m.ageRanges,
        })),
        log: {
            event: EventLogs.PRESCRIPTION_REMADE,
            details: {
                id: internalId,
                originalSequenceId: sequence.id,
                ...itemsDetail(valueItem('remake_of', sequence.internalId)),
            },
        },
    }, ctx);
    // mark the original so a remake can only be started once
    await tx.mutate.patientTherapySequence.update({
        id: sequence.id,
        remadeBy: ctx.user.id,
        remadeAt: Date.now(),
        remadeToId: remadeSequenceId,
        updatedAt: Date.now(),
        updatedBy: ctx.user.id,
    });
}
async function applyAbortBatchEffect(tx, payload, ctx) {
    assertIsAuthenticated(ctx);
    const { batch, materials, sequenceId } = payload;
    const catalogType = batch.tradeName?.medicationProfile?.drugCatalogItem?.catalogType;
    const isCarrier = catalogType === CatalogType.enum.SOLUTION;
    const isActiveSubstance = catalogType === CatalogType.enum.ACTIVE_SUBSTANCE;
    // Restock intact units with a single quantity update: active substances use UNUSED, consumables and carriers use INTACT.
    const intactCount = materials.filter(m => (isActiveSubstance ? m.status === 'UNUSED' : m.status === 'INTACT')).length;
    if (intactCount > 0) {
        const now = Date.now();
        const updatedBatch = {
            id: batch.id,
            quantity: Math.min((batch.quantity ?? 0) + intactCount, batch.receivedQuantity ?? Number.MAX_SAFE_INTEGER),
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.batches.update(updatedBatch);
        await recordStockMovement(tx, ctx, {
            type: StockMovementType.enum.corrected,
            batchId: batch.id,
            quantity: intactCount,
            source: StockMovementSource.enum.new_pack,
            sequenceId,
        }, now);
        if (tx.location === 'server') {
            const patches = createPatch({ quantity: batch.quantity }, { quantity: updatedBatch.quantity });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.batch,
                resourceId: batch.id,
                action: 'update',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                domain: 'batch',
                status: 'OK',
                details: {
                    batchId: batch.lotNumber,
                },
            }, ctx);
        }
    }
    // Consumables and carriers never produce rest vials or discards.
    if (!isActiveSubstance || isCarrier)
        return;
    await materials.reduce(async (chain, material) => {
        await chain;
        switch (material.status) {
            case 'PARTIALLY_CONSUMED':
                if (material.remainingWeightVolume == null)
                    throw new Error(`remainingWeightVolume is required for partially consumed batch ${batch.id}`);
                if (batch.tradeName?.strengthValue != null && material.remainingWeightVolume > batch.tradeName.strengthValue)
                    throw new Error(`remainingWeightVolume cannot exceed the initial amount (${batch.tradeName.strengthValue}) for batch ${batch.id}`);
                await createRestVialFromBatch(tx, { batchId: batch.id, amount: material.remainingWeightVolume, sequenceId }, ctx);
                break;
            case 'SCRAPPED':
                await createDiscard(tx, { type: 'batch', id: batch.id, stockLocationId: batch.stockLocationId }, 'ABORTED', ctx);
                break;
        }
    }, Promise.resolve());
}
async function applyAbortRestVialEffect(tx, payload, ctx) {
    assertIsAuthenticated(ctx);
    const { restVial, material } = payload;
    const now = Date.now();
    const userActions = {
        updatedAt: now,
        updatedBy: ctx.user.id,
    };
    switch (material.status) {
        case 'UNUSED': {
            // a vial discarded meanwhile must not return to stock
            if (restVial.status !== RestVialStatus.enum.IN_USE || restVial.deletedAt != null)
                break;
            const updatedRestVial = { id: restVial.id, status: RestVialStatus.enum.AVAILABLE, ...userActions };
            await tx.mutate.restVials.update(updatedRestVial);
            if (tx.location === 'server') {
                const patches = createPatch(restVial, updatedRestVial);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.restVial,
                    resourceId: restVial.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: {},
                }, ctx);
            }
            break;
        }
        case 'PARTIALLY_CONSUMED': {
            if (material.remainingWeightVolume == null)
                throw new Error(`remainingWeightVolume is required for partially consumed rest vial ${restVial.id}`);
            if (restVial.amount != null && material.remainingWeightVolume > restVial.amount)
                throw new Error(`remainingWeightVolume cannot exceed the current amount (${restVial.amount}) for rest vial ${restVial.id}`);
            const updatedRestVial = { id: restVial.id, amount: material.remainingWeightVolume, ...userActions };
            await tx.mutate.restVials.update(updatedRestVial);
            if (tx.location === 'server') {
                const patches = createPatch(restVial, updatedRestVial);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.restVial,
                    resourceId: restVial.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: {},
                }, ctx);
            }
            break;
        }
        case 'SCRAPPED': {
            const updatedRestVial = { id: restVial.id, status: RestVialStatus.enum.DISCARDED, ...userActions };
            await tx.mutate.restVials.update(updatedRestVial);
            await createDiscard(tx, { type: 'restVial', id: restVial.id, stockLocationId: restVial.stockLocationId }, 'ABORTED', ctx);
            await logAmmsRestVialDiscardMessage(tx, { restVialId: restVial.id, now });
            if (tx.location === 'server') {
                const patches = createPatch({}, updatedRestVial);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.restVial,
                    resourceId: restVial.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: {},
                }, ctx);
            }
            break;
        }
    }
}
async function applyAbortReturnEffect(tx, payload, ctx) {
    assertIsAuthenticated(ctx);
    const { returnItem, material } = payload;
    const now = Date.now();
    const userActions = {
        updatedAt: now,
        updatedBy: ctx.user.id,
    };
    switch (material.status) {
        case 'UNUSED': {
            // Never opened — restock it as available.
            const updatedReturn = { id: returnItem.id, status: ReturnStatus.enum.AVAILABLE, ...userActions };
            await tx.mutate.returns.update(updatedReturn);
            if (tx.location === 'server') {
                const patches = createPatch(returnItem, updatedReturn);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.return,
                    resourceId: returnItem.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: {},
                }, ctx);
            }
            break;
        }
        case 'PARTIALLY_CONSUMED':
        case 'SCRAPPED': {
            // Opened/used as starting material — a prepared bag cannot go back to inventory.
            const updatedReturn = { id: returnItem.id, status: ReturnStatus.enum.DISCARDED, ...userActions };
            await tx.mutate.returns.update(updatedReturn);
            await createDiscard(tx, { type: 'return', id: returnItem.id, stockLocationId: returnItem.stockLocationId }, 'ABORTED', ctx);
            if (tx.location === 'server') {
                const patches = createPatch(returnItem, updatedReturn);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.return,
                    resourceId: returnItem.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: {},
                }, ctx);
            }
            break;
        }
    }
}
async function recordProductionWeighings(tx, sequenceId, weighings, userId) {
    if (!weighings.length)
        return;
    // a replayed COMPLETE_PRODUCTION must not duplicate the weighings
    const existing = await tx.run(zql.productionDetails
        .where('therapySequenceId', '=', sequenceId)
        .where('step', '!=', ProductionDetailStep.enum.PREPARATION_START)
        .one());
    if (existing)
        return;
    // each reading keeps the user who took it; an unknown or missing user falls back to the one completing
    const readerIds = [...new Set(weighings.flatMap(weighing => (weighing.recordedBy ? [weighing.recordedBy] : [])))];
    const knownReaders = readerIds.length ? await tx.run(zql.user.where('id', 'IN', readerIds)) : [];
    const knownReaderIds = new Set(knownReaders.map(reader => reader.id));
    const now = Date.now();
    await Promise.all(weighings.map(weighing => tx.mutate.productionDetails.insert({
        id: uuidv7(),
        therapySequenceId: sequenceId,
        medicineId: weighing.medicineId,
        step: weighing.step,
        substanceIndex: weighing.substanceIndex,
        allocatedDose: weighing.allocatedDose,
        expectedWeightGrams: weighing.expectedWeightGrams,
        actualWeightGrams: weighing.actualWeightGrams,
        recordedAt: weighing.recordedAt,
        batchId: weighing.batchId ?? null,
        scaleMemoryNumber: weighing.scaleMemoryNumber ?? null,
        createdAt: now,
        createdBy: weighing.recordedBy && knownReaderIds.has(weighing.recordedBy) ? weighing.recordedBy : userId,
    })));
}
async function logProductionEvent(tx, payload) {
    if (tx.location !== 'server' || !payload.sequence.orderId)
        return;
    await addEventLog(tx, {
        id: uuidv7(),
        resource: EventLogResources.order,
        resourceId: payload.sequence.orderId,
        action: 'update',
        domain: 'production',
        event: payload.event,
        performedBy: payload.userId,
        performedAt: Date.now(),
        status: 'OK',
        details: {
            orderInternalId: payload.sequence.order?.internalId,
            productName: buildProductName(payload.sequence),
        },
    });
}
//# sourceMappingURL=patients-therapies.mutators.js.map